/**
 * MSAL configuration utilities.
 * Framework-agnostic functions for building MSAL configuration.
 */
import { LogLevel } from '@azure/msal-browser';
/**
 * Map log level string to MSAL LogLevel enum.
 */
export function getLogLevel(level) {
    switch (level.toLowerCase()) {
        case 'error':
            return LogLevel.Error;
        case 'warning':
            return LogLevel.Warning;
        case 'info':
            return LogLevel.Info;
        case 'verbose':
            return LogLevel.Verbose;
        case 'trace':
            return LogLevel.Trace;
        default:
            return LogLevel.Warning;
    }
}
/**
 * Derive MSAL authority from the authApi (OpenID configuration URL).
 *
 * For B2C: https://tenant.b2clogin.com/tenant.onmicrosoft.com/policy/v2.0/.well-known/openid-configuration
 * Authority: https://tenant.b2clogin.com/tenant.onmicrosoft.com/policy
 *
 * For AAD: https://login.microsoftonline.com/tenant-id/v2.0/.well-known/openid-configuration
 * Authority: https://login.microsoftonline.com/tenant-id
 */
export function deriveAuthorityFromAuthApi(authApi) {
    try {
        // Remove the /.well-known/openid-configuration suffix
        const authority = authApi
            .replace(/\/v2\.0\/\.well-known\/openid-configuration$/i, '')
            .replace(/\/\.well-known\/openid-configuration$/i, '');
        return authority;
    }
    catch {
        // Fallback to organizations endpoint
        return 'https://login.microsoftonline.com/organizations';
    }
}
/**
 * Check if the auth API URL is Azure B2C.
 */
export function isB2CAuthApi(authApi) {
    try {
        const { hostname, pathname } = new URL(authApi);
        if (hostname.includes('b2clogin.com')) {
            return true;
        }
        const pathSegments = pathname.split('/').filter(Boolean);
        const v2Index = pathSegments.findIndex(segment => segment.toLowerCase() === 'v2.0');
        // AAD authorities look like /{tenant}/v2.0/.well-known/openid-configuration.
        // B2C authorities include additional path segments such as /{tenant}/{policy}/v2.0/...
        return v2Index > 1;
    }
    catch {
        return false;
    }
}
/**
 * Build MSAL configuration from auth config.
 */
export function buildMsalConfig(options) {
    const { authConfig, logCallback } = options;
    const derivedAuthority = deriveAuthorityFromAuthApi(authConfig.authApi);
    const isB2C = isB2CAuthApi(authConfig.authApi);
    // Prefer explicit overrides from config, fall back to derived values
    const authority = authConfig.authority ?? derivedAuthority;
    const knownAuthorities = authConfig.knownAuthorities ?? (isB2C ? [new URL(authConfig.authApi).hostname] : undefined);
    const config = {
        auth: {
            clientId: authConfig.clientId,
            authority,
            redirectUri: authConfig.redirectUri,
            postLogoutRedirectUri: authConfig.postLogoutRedirectUri,
            navigateToLoginRequestUrl: true,
            knownAuthorities
        },
        cache: {
            // Use localStorage if tokensToLocalStorage is true, otherwise sessionStorage
            cacheLocation: authConfig.tokensToLocalStorage ? 'localStorage' : 'sessionStorage',
            storeAuthStateInCookie: false
        },
        system: {
            loggerOptions: {
                loggerCallback: logCallback ??
                    ((level, message, containsPii) => {
                        if (containsPii)
                            return;
                        if (!authConfig.disabled) {
                            console.log(`[MSAL] ${message}`);
                        }
                    }),
                logLevel: getLogLevel(authConfig.logLevel ?? 'warning')
            }
        }
    };
    return { config, authority, isB2C };
}
/**
 * Default login scopes for OIDC authentication.
 */
export const DEFAULT_LOGIN_SCOPES = ['openid', 'profile', 'email'];
/**
 * Home Realm Discovery: resolve authority for an email address.
 *
 * This function determines which Azure AD tenant to authenticate against
 * based on the user's email domain.
 *
 * @param email - User's email address
 * @param domainMappings - Optional custom domain-to-tenant mappings
 * @param defaultAuthority - Default authority when no mapping matches
 * @returns The MSAL authority URL for the email's domain
 */
export function resolveAuthorityForEmail(email, domainMappings = [], defaultAuthority) {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) {
        return defaultAuthority;
    }
    // Check custom domain mappings first
    const mapping = domainMappings.find(m => m.domain.toLowerCase() === domain);
    if (mapping) {
        return mapping.authority;
    }
    // Return default authority
    return defaultAuthority;
}
/**
 * Extract domain from email address.
 */
export function getDomainFromEmail(email) {
    return email.split('@')[1]?.toLowerCase() ?? '';
}
/**
 * Validate email format for HRD.
 */
export function isValidEmailForHrd(email) {
    const domain = getDomainFromEmail(email);
    return email.includes('@') && domain.length > 1;
}
/**
 * Create a complete auth setup from auth configuration.
 *
 * This is the recommended way to initialize auth in consuming apps.
 * Returns everything needed to wire up authentication in one call.
 *
 * @example
 * ```ts
 * import { createAuthSetup } from '@platform-ui/auth';
 * import { env } from './config/env';
 *
 * export const {
 *     msalConfig,
 *     defaultAuthority,
 *     isAuthDisabled,
 *     hrdEnabled,
 *     apiScopes
 * } = createAuthSetup(env.auth);
 * ```
 */
export function createAuthSetup(authConfig) {
    const { config, authority, isB2C } = buildMsalConfig({ authConfig });
    return {
        msalConfig: config,
        defaultAuthority: authority,
        isB2C,
        isAuthDisabled: authConfig.disabled ?? false,
        hrdEnabled: authConfig.homeRealmDiscovery ?? false,
        apiScopes: authConfig.scope ? [authConfig.scope] : []
    };
}
//# sourceMappingURL=config.js.map